import { useCallback, useMemo, useState } from "react";

import Field from "../../../shared/ui/Field/Field";
import TextInput from "../../../shared/ui/Field/TextInput";
import Select from "../../../shared/ui/Field/Select";
import Textarea from "../../../shared/ui/Field/Textarea";
import InlineError from "../../../shared/ui/Field/InlineError";
import Spinner from "../../../shared/ui/Loading/Spinner";

import { createClient } from "../api/clients";
import {
  parseDrfFieldErrors,
  messageFromError,
} from "../../../shared/api/drfErrors";

// ClientCreateForm — feature-local widget. Owns the POST-create
// mutation lifecycle for a single new client: validate → submit →
// success / field-error / form-error.
//
// CL-5 doctrine class: R8 §4 T-1 form (mutation owner). Parent
// (ClientsListScreenContent) owns open-state + post-success
// refetch; this form owns ONLY the mutation. No baseline / no
// PATCH-diff (that's the edit shape). No optimistic insertion.
// No success-toast registry. No callbacks beyond onSuccess.
//
// Field shape mirrors the canonical client-form in
// design/clients.html — first_name, last_name, phone, email, type,
// status, source, notes. Backed verbatim by ClientWriteSerializer.

const TYPE_OPTIONS = [
  { value: "buyer", label: "Покупець" },
  { value: "seller", label: "Продавець" },
  { value: "both", label: "Покупець і продавець" },
];

const STATUS_OPTIONS = [
  { value: "lead", label: "Лід" },
  { value: "active", label: "Активний" },
  { value: "inactive", label: "Неактивний" },
  { value: "closed", label: "Закритий" },
];

const TYPE_VALUES = TYPE_OPTIONS.map((o) => o.value);
const STATUS_VALUES = STATUS_OPTIONS.map((o) => o.value);

const FIELD_KEYS = [
  "first_name",
  "last_name",
  "phone",
  "email",
  "type",
  "status",
  "source",
  "notes",
];

const FIRST_NAME_MAX = 128;
const LAST_NAME_MAX = 128;
const PHONE_MAX = 32;
const SOURCE_MAX = 128;
const NOTES_MAX = 2000;

const INITIAL_VALUES = {
  first_name: "",
  last_name: "",
  phone: "",
  email: "",
  type: "buyer",
  status: "lead",
  source: "",
  notes: "",
};

const looksLikeEmail = (value) => /.+@.+\..+/.test(value);

// Feature-local validator. Mirrors ClientEditForm's validator on the
// shared fields; explicit function, no schema engine, no shared
// validation registry.
const validateClient = (values) => {
  const errors = {};

  const firstName = (values.first_name ?? "").trim();
  if (firstName.length === 0) {
    errors.first_name = "Введіть ім'я.";
  } else if (firstName.length > FIRST_NAME_MAX) {
    errors.first_name = `Не довше ${FIRST_NAME_MAX} символів.`;
  }

  const lastName = values.last_name ?? "";
  if (lastName.length > LAST_NAME_MAX) {
    errors.last_name = `Не довше ${LAST_NAME_MAX} символів.`;
  }

  const phone = (values.phone ?? "").trim();
  if (phone.length === 0) {
    errors.phone = "Введіть телефон.";
  } else if (phone.length > PHONE_MAX) {
    errors.phone = `Не довше ${PHONE_MAX} символів.`;
  }

  const email = (values.email ?? "").trim();
  if (email && !looksLikeEmail(email)) {
    errors.email = "Введіть коректну електронну адресу.";
  }

  const type = values.type ?? "";
  if (!TYPE_VALUES.includes(type)) {
    errors.type = "Виберіть тип.";
  }

  const status = values.status ?? "";
  if (!STATUS_VALUES.includes(status)) {
    errors.status = "Виберіть статус.";
  }

  const source = values.source ?? "";
  if (source.length > SOURCE_MAX) {
    errors.source = `Не довше ${SOURCE_MAX} символів.`;
  }

  const notes = values.notes ?? "";
  if (notes.length > NOTES_MAX) {
    errors.notes = `Не довше ${NOTES_MAX} символів.`;
  }

  return errors;
};

const omitKey = (obj, key) => {
  if (!obj || obj[key] === undefined) return obj || {};
  const next = { ...obj };
  delete next[key];
  return next;
};

const omitKeys = (obj, keys) => {
  if (!obj) return obj || {};
  let next = obj;
  let mutated = false;
  for (const key of keys) {
    if (next[key] !== undefined) {
      if (!mutated) {
        next = { ...obj };
        mutated = true;
      }
      delete next[key];
    }
  }
  return next;
};

const buildCreateBody = (values) => {
  const body = {};
  for (const key of FIELD_KEYS) {
    body[key] = values[key] ?? "";
  }
  return body;
};

function ClientCreateForm({ onSuccess, onCancel }) {
  const [values, setValues] = useState(INITIAL_VALUES);
  const [saveState, setSaveState] = useState("idle");
  const [fieldErrors, setFieldErrors] = useState({});
  const [clientErrors, setClientErrors] = useState({});

  const handleValueChange = useCallback((name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    setClientErrors((prev) => omitKey(prev, name));
    setFieldErrors((prev) => omitKeys(prev, [name, "_form"]));
  }, []);

  const saving = saveState === "saving";

  const clientErrorsCount = Object.keys(clientErrors).length;
  const canSubmit = !saving && clientErrorsCount === 0;

  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    if (saving) return;

    const errs = validateClient(values);
    if (Object.keys(errs).length > 0) {
      setClientErrors(errs);
      setFieldErrors({});
      return;
    }

    setClientErrors({});
    setFieldErrors({});
    setSaveState("saving");

    try {
      const created = await createClient(buildCreateBody(values));
      setSaveState("saved");
      if (onSuccess) {
        onSuccess(created);
      }
    } catch (err) {
      setFieldErrors(parseDrfFieldErrors(err, FIELD_KEYS));
      setSaveState("error");
    }
  };

  const formError = fieldErrors._form;
  const errorOf = (name) => clientErrors[name] || fieldErrors[name];

  const onChange = (name) => (event) => handleValueChange(name, event.target.value);

  // Derive a stable id-prefix so multiple instances would not collide.
  // No shared id-registry, no useId wrapper — single feature-local
  // constant suffices because the modal renders at most one form.
  const fieldId = useMemo(() => (name) => `client-create-${name}`, []);

  return (
    <form
      className="ui-form"
      data-feature="client-create-form"
      onSubmit={handleSubmit}
      noValidate
    >
      <div className="ui-form__rows">
        <div className="feature-clients-list__pair">
          <Field
            id={fieldId("first_name")}
            label="Ім'я"
            error={errorOf("first_name")}
            required
          >
            <TextInput
              name="first_name"
              type="text"
              value={values.first_name}
              onChange={onChange("first_name")}
              maxLength={FIRST_NAME_MAX}
              autoComplete="given-name"
              disabled={saving}
              placeholder="Олег"
            />
          </Field>

          <Field
            id={fieldId("last_name")}
            label="Прізвище"
            error={errorOf("last_name")}
          >
            <TextInput
              name="last_name"
              type="text"
              value={values.last_name}
              onChange={onChange("last_name")}
              maxLength={LAST_NAME_MAX}
              autoComplete="family-name"
              disabled={saving}
              placeholder="Грищенко"
            />
          </Field>
        </div>

        <div className="feature-clients-list__pair">
          <Field
            id={fieldId("phone")}
            label="Телефон"
            help="Унікальний у межах вашого облікового запису."
            error={errorOf("phone")}
            required
          >
            <TextInput
              name="phone"
              type="tel"
              value={values.phone}
              onChange={onChange("phone")}
              maxLength={PHONE_MAX}
              autoComplete="tel"
              placeholder="+380 50 123 45 67"
              disabled={saving}
            />
          </Field>

          <Field
            id={fieldId("email")}
            label="Email"
            error={errorOf("email")}
          >
            <TextInput
              name="email"
              type="email"
              value={values.email}
              onChange={onChange("email")}
              autoComplete="email"
              placeholder="client@example.com"
              disabled={saving}
            />
          </Field>
        </div>

        <div className="feature-clients-list__pair">
          <Field
            id={fieldId("type")}
            label="Тип"
            error={errorOf("type")}
            required
          >
            <Select
              name="type"
              value={values.type}
              onChange={onChange("type")}
              options={TYPE_OPTIONS}
              placeholder="Виберіть тип…"
              disabled={saving}
            />
          </Field>

          <Field
            id={fieldId("status")}
            label="Статус"
            error={errorOf("status")}
            required
          >
            <Select
              name="status"
              value={values.status}
              onChange={onChange("status")}
              options={STATUS_OPTIONS}
              placeholder="Виберіть статус…"
              disabled={saving}
            />
          </Field>
        </div>

        <Field
          id={fieldId("source")}
          label="Джерело"
          help="Звідки клієнт прийшов: рекомендація, реклама, тощо."
          error={errorOf("source")}
        >
          <TextInput
            name="source"
            type="text"
            value={values.source}
            onChange={onChange("source")}
            maxLength={SOURCE_MAX}
            autoComplete="off"
            placeholder="Instagram, Реферал, дзвінок з оголошення…"
            disabled={saving}
          />
        </Field>

        <Field
          id={fieldId("notes")}
          label="Нотатки"
          help={`До ${NOTES_MAX} символів. Простий текст.`}
          error={errorOf("notes")}
        >
          <Textarea
            name="notes"
            value={values.notes}
            onChange={onChange("notes")}
            rows={4}
            maxLength={NOTES_MAX}
            placeholder="Внутрішні нотатки про клієнта…"
            disabled={saving}
          />
        </Field>
      </div>

      <div className="ui-form__messages">
        {formError ? (
          <InlineError className="ui-form__message">{formError}</InlineError>
        ) : null}
      </div>

      <div className="ui-form__actions">
        <button
          type="button"
          className="ui-form__button ui-form__button--ghost"
          onClick={onCancel}
          disabled={saving}
        >
          Скасувати
        </button>
        <button
          type="submit"
          className="ui-form__button ui-form__button--primary"
          disabled={!canSubmit}
          data-loading={saving ? "true" : undefined}
        >
          {saving ? <Spinner size="sm" label="Створення" /> : "Створити"}
        </button>
      </div>
    </form>
  );
}

export default ClientCreateForm;
